/** Разбор ответов API и типы данных, общие для экранов. */

export type AcquisitionType =
  | 'found' | 'purchased' | 'traded' | 'gift' | 'inherited' | 'unknown';

export type Confidence = 'confirmed' | 'probable' | 'uncertain' | 'as_labelled';

export type Me = {
  id: string;
  email: string;
  display_name: string | null;
  inv_prefix: string;
  inv_padding: number;
  inv_counter: number;
  default_currency: string;
  hide_prices: boolean;
  home_latitude: string | null;
  home_longitude: string | null;
  home_radius_m: number;
  ai_enabled: boolean;
};

export type SpecimenListItem = {
  id: string;
  inv_number: string;
  public_id: string;
  trade_name: string | null;
  acquisition_type: AcquisitionType;
  acquired_on: string | null;
  created_at: string;
  primary_mineral: string | null;
  primary_mineral_ru: string | null;
  variety: string | null;
  locality_name: string | null;
  country: string | null;
  inherited_from_trip: boolean | null;
  cover_photo_id: string | null;
};

export type PhotoKind = 'specimen' | 'label' | 'in_situ' | 'macro' | 'uv';

export type Photo = {
  id: string;
  kind: PhotoKind;
  caption: string | null;
  width: number | null;
  height: number | null;
  taken_at: string | null;
  exif_latitude: string | null;
  exif_longitude: string | null;
  is_cover: boolean;
};

export type SpecimenMineral = {
  mineral_id: number;
  name: string;
  name_ru: string | null;
  kind: string;
  mindat_id: number | null;
  formula: string | null;
  hardness_min: string | null;
  hardness_max: string | null;
  crystal_system: string | null;
  is_primary: boolean;
  confidence: Confidence;
  note: string | null;
};

export type Valuation = {
  id: string;
  valued_on: string;
  amount: string;
  currency: string;
  basis: string | null;
  note: string | null;
  created_at?: string;
};

export type Specimen = {
  id: string;
  inv_number: string;
  public_id: string;
  trade_name: string | null;
  locality_note: string | null;
  acquisition_type: AcquisitionType;
  acquired_on: string | null;
  acquired_from: string | null;
  acquired_place: string | null;
  trip_id: string | null;

  find_latitude: string | null;
  find_longitude: string | null;

  size_x_mm: string | null;
  size_y_mm: string | null;
  size_z_mm: string | null;
  weight_g: string | null;

  // Деньги вырезаются на сервере, когда включён режим «без цен» (ТЗ D4):
  // здесь придёт null, а prices_hidden скажет, что это не «не заполнено».
  purchase_amount: string | null;
  purchase_currency: string | null;
  latest_valuation: Valuation | null;
  valuation_count: number;
  prices_hidden?: boolean;

  storage_location: string | null;
  condition_note: string | null;
  notes: string | null;

  // из specimen_location: место с учётом наследования от выезда
  effective_latitude: string | null;
  effective_longitude: string | null;
  effective_coord_source: string | null;
  effective_precision_m: number | null;
  inherited_from_trip: boolean | null;

  /** Собственное месторождение образца; null, когда оно берётся от выезда. */
  locality_id: number | null;
  locality_name: string | null;
  locality_full_path: string | null;
  locality_country: string | null;
  locality_region: string | null;

  trip_title: string | null;
  trip_started_on: string | null;
  trip_companions: string | null;
  trip_sibling_count: number;

  cover_photo_id: string | null;
  minerals: SpecimenMineral[];
  photos: Photo[];
};

export type Trip = {
  id: string;
  title: string | null;
  started_on: string;
  ended_on: string | null;
  locality_name: string | null;
  locality_country: string | null;
  locality_note: string | null;
  latitude: string | null;
  longitude: string | null;
  coord_precision_m: number | null;
  companions: string | null;
  notes: string | null;
  specimen_count: number;
};

export type ExportStatus = {
  last_export_at: string | null;
  days_since: number | null;
  remind: boolean;
  remind_after_days: number;
};

export type Summary = {
  specimens: number;
  species: number;
  countries: number;
  found: number;
  purchased: number;
};

export type LabelTemplate = {
  key: string;
  title: string;
  hint: string;
  /** Лист A4 с сеткой наклеек или лента в термопринтере. */
  kind: 'sheet' | 'roll';
  /** Только у ленты: ширина печатающей головки и её разрешение. */
  printWidth?: number;
  dpi?: number;
  page: { width: number; height: number };
  label: { width: number; height: number };
  margin: { left: number; top: number };
  gap: { x: number; y: number };
  columns: number;
  rows: number;
  qr: number;
  cutMarks: boolean;
};

export type LabelPreview = {
  id: string;
  url: string;
  title: string;
  place: string;
  footer: string;
  /**
   * Матрица кода строками из нулей и единиц. Нужна, чтобы нарисовать
   * этикетку для термопринтера попиксельно: масштабировать готовую
   * картинку кода нельзя, модуль размажется по точкам головки.
   */
  qr?: { size: number; rows: string[] };
};

export type ReferenceStatus = {
  status: 'idle' | 'running' | 'done' | 'failed';
  page: number;
  imported: number;
  message: string | null;
  started_at: string | null;
  updated_at: string;
  token: boolean;
  minerals: number;
  species: number;
  varieties: number;
  with_russian: number;
  unlinked: number;
};

export type MineralSuggestion = {
  id: number;
  name: string;
  name_ru: string | null;
  kind: string;
  mindat_id: number | null;
  formula: string | null;
  hardness_min: string | null;
  hardness_max: string | null;
  parent_name: string | null;
  parent_name_ru: string | null;
};

export type UnlinkedMineral = {
  id: string;
  name: string;
  uses: number;
  candidates: {
    id: string;
    name: string;
    name_ru: string | null;
    kind: string;
    mindat_id: number;
  }[];
};

export type MapPointRow = {
  id: string;
  inv_number: string;
  latitude: number;
  longitude: number;
  coord_source: string | null;
  precision_m: number | null;
  inherited_from_trip: boolean | null;
  cover_photo_id: string | null;
  acquisition_type: AcquisitionType;
  year: number | null;
  primary_mineral: string | null;
  primary_mineral_ru: string | null;
  locality_name: string | null;
  country: string | null;
  region: string | null;
  trip_id: string | null;
  trip_title: string | null;
};

export type MapAnswer = {
  points: MapPointRow[];
  hidden_by_home_zone: number;
  home_zone_set: boolean;
};

export type PhotoCoords = {
  candidates: {
    photo_id: string;
    kind: PhotoKind;
    taken_at: string | null;
    latitude: number;
    longitude: number;
  }[];
  spread_m: number;
  conflicting: boolean;
  offer: boolean;
  dismissed: boolean;
  hidden_by_home_zone: number;
};

export type ProvenanceEntry = {
  id: string;
  sort_order: number;
  owner_name: string;
  period_note: string | null;
  note: string | null;
};

export type AiStatus = {
  /** Ключ есть на сервере. Без него кнопка не показывается вовсе. */
  configured: boolean;
  /** Владелец разрешил отправлять снимки наружу. */
  enabled: boolean;
  model: string;
  via_proxy: boolean;
};

export type IdCandidate = {
  name: string;
  confidence: number;
  why: string;
  distinguish: string;
  test: string;
  mineral_id: number | null;
  name_ru: string | null;
  formula: string | null;
  hardness: string | null;
  in_locality: boolean;
};

export type Identification = {
  id: string;
  specimen_id: string;
  model: string;
  photo_ids: string[];
  /** Дословно то, что ушло наружу: факты об образце и список месторождения. */
  context: { facts?: string; locality_minerals?: number; hint?: string | null } | null;
  /** Версии о главном минерале: взаимоисключающие. */
  candidates: IdCandidate[];
  /** Спутники на том же образце: не спорят ни с главным, ни друг с другом. */
  others: IdCandidate[];
  missing: string | null;
  status: 'ok' | 'failed';
  error: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  elapsed_ms: number | null;
  accepted_mineral_id: number | null;
  accepted_at: string | null;
  created_at: string;
};

export type PriceGuess = {
  low: number;
  likely: number;
  high: number;
  why: string;
  confidence: number;
  model: string;
  /** Оговорка, привязанная к самой сумме: на краях диапазона оценка врёт. */
  caveat: string | null;
};

export type Appraisal = {
  mineral: string;
  /** Назван владельцем или определён по снимку — это разные вещи. */
  mineral_guessed: boolean;
  identification: { candidates: IdCandidate[]; others: IdCandidate[]; missing: string } | null;
  price: PriceGuess;
};

export type MoneyTotal = { currency: string; amount: number; count: number };

export type Stats = Summary & {
  other: number;
  with_photo: number;
  with_locality: number;
  with_coordinates: number;
  trips: number;
  photos: number;
  by_country: { country: string; count: number }[];
  by_mineral: { name: string; name_ru: string | null; count: number }[];
  by_confidence: { confidence: Confidence; count: number }[];
  by_year: { year: number; count: number }[];
  prices_hidden: boolean;
  money: { purchase: MoneyTotal[]; valuation: MoneyTotal[]; currency: string } | null;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    // Кука сессии — httpOnly, браузер шлёт её сам; same-origin здесь явно,
    // чтобы поведение не зависело от умолчаний.
    credentials: 'same-origin',
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const message =
      typeof body === 'object' && body && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Ошибка ${response.status}`;
    throw new ApiError(response.status, message);
  }

  return body as T;
}

const json = (body: unknown): RequestInit['body'] => JSON.stringify(body);

export const api = {
  setupNeeded: () => request<{ setupNeeded: boolean }>('/auth/setup-needed'),
  register: (email: string, password: string) =>
    request<Me>('/auth/register', { method: 'POST', body: json({ email, password }) }),
  login: (email: string, password: string) =>
    request<Me>('/auth/login', { method: 'POST', body: json({ email, password }) }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<Me>('/auth/me'),
  updateProfile: (body: Record<string, unknown>) =>
    request<Me>('/profile', { method: 'PATCH', body: json(body) }),

  specimens: (query: Record<string, string | undefined> = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value) params.set(key, value);
    }
    const suffix = params.toString();
    return request<SpecimenListItem[]>(`/specimens${suffix ? `?${suffix}` : ''}`);
  },
  summary: () => request<Summary>('/specimens/summary'),
  specimen: (id: string) => request<Specimen>(`/specimens/${id}`),
  specimenByPublicId: (publicId: string) =>
    request<Specimen>(`/specimens/by-public-id/${publicId}`),
  createSpecimen: (body: unknown) =>
    request<Specimen>('/specimens', { method: 'POST', body: json(body) }),
  updateSpecimen: (id: string, body: unknown) =>
    request<Specimen>(`/specimens/${id}`, { method: 'PATCH', body: json(body) }),
  deleteSpecimen: (id: string) => request<void>(`/specimens/${id}`, { method: 'DELETE' }),

  trips: () => request<Trip[]>('/trips'),
  trip: (id: string) => request<Trip & { specimens: SpecimenListItem[] }>(`/trips/${id}`),
  createTrip: (body: unknown) => request<Trip>('/trips', { method: 'POST', body: json(body) }),
  updateTrip: (id: string, body: unknown) =>
    request<Trip>(`/trips/${id}`, { method: 'PATCH', body: json(body) }),

  mineralSuggestions: (q: string) =>
    request<MineralSuggestion[]>(`/minerals?q=${encodeURIComponent(q)}`),
  localitySuggestions: (q: string) =>
    request<{
      id: number;
      name: string;
      name_ru: string | null;
      country: string | null;
      region: string | null;
      full_path: string | null;
      mindat_id: number | null;
    }[]>(`/localities?q=${encodeURIComponent(q)}`),

  // ── Справочник Mindat ───────────────────────────────────────────
  referenceStatus: () => request<ReferenceStatus>('/reference/status'),
  startReferenceSync: (only?: 'russian') =>
    request<ReferenceStatus>(`/reference/sync${only ? `?only=${only}` : ''}`, { method: 'POST' }),
  relinkMinerals: () =>
    request<{ linked: { from: string; to: string }[]; left: number }>('/reference/relink', {
      method: 'POST',
    }),
  unlinkedMinerals: () => request<{ unlinked: UnlinkedMineral[] }>('/reference/unlinked'),
  linkMineral: (id: string, targetId: string) =>
    request<void>(`/minerals/${id}/link`, {
      method: 'POST',
      body: json({ target_id: Number(targetId) }),
    }),
  regions: (country?: string) =>
    request<{ region: string; country: string; count: number }[]>(
      `/regions${country ? `?country=${encodeURIComponent(country)}` : ''}`,
    ),
  countries: () => request<{ country: string; count: number }[]>('/countries'),
  stats: () => request<Stats>('/stats'),

  // ── Карта (ТЗ 9) ────────────────────────────────────────────────
  mapPoints: (query: Record<string, string | undefined> = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value) params.set(key, value);
    const suffix = params.toString();
    return request<MapAnswer>(`/map${suffix ? `?${suffix}` : ''}`);
  },
  photoCoords: (specimenId: string) =>
    request<PhotoCoords>(`/specimens/${specimenId}/photo-coords`),
  dismissCoordHint: (specimenId: string) =>
    request<void>(`/specimens/${specimenId}/dismiss-coord-hint`, { method: 'POST' }),

  valuations: (specimenId: string) =>
    request<{ prices_hidden: boolean; valuations: Valuation[] }>(
      `/specimens/${specimenId}/valuations`,
    ),
  addValuation: (specimenId: string, body: Record<string, unknown>) =>
    request<Valuation>(`/specimens/${specimenId}/valuations`, {
      method: 'POST',
      body: json(body),
    }),
  deleteValuation: (id: string) => request<void>(`/valuations/${id}`, { method: 'DELETE' }),

  exportStatus: () => request<ExportStatus>('/export/status'),

  /** Файлы уходят как FormData, поэтому Content-Type ставит браузер сам. */
  uploadPhoto: (specimenId: string, body: FormData) =>
    fetch(`/api/specimens/${specimenId}/photos`, {
      method: 'POST',
      body,
      credentials: 'same-origin',
    }).then(async (response) => {
      if (response.ok) return (await response.json()) as Photo;
      const text = await response.text();
      let message = `Ошибка ${response.status}`;
      try {
        const parsed: unknown = JSON.parse(text);
        if (typeof parsed === 'object' && parsed && 'error' in parsed) {
          message = String((parsed as { error: unknown }).error);
        }
      } catch {
        /* тело не JSON — оставляем код статуса */
      }
      throw new ApiError(response.status, message);
    }),
  // ── Этикетки ────────────────────────────────────────────────────
  labelTemplates: () =>
    request<{ templates: LabelTemplate[]; origin: string }>('/labels/templates'),
  labelPreview: (ids?: string) =>
    request<{ labels: LabelPreview[] }>(`/labels/preview${ids ? `?ids=${ids}` : ''}`),
  qrUrl: (specimenId: string) => `/api/specimens/${specimenId}/qr.svg`,

  /**
   * PDF приходит потоком и сразу уходит в файл: держать лист этикеток
   * в памяти вкладки незачем, а имя файла должно быть с датой.
   */
  labelPdf: async (body: { ids: string[]; template: string; skip: number }) => {
    const response = await fetch('/api/labels/pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const text = await response.text();
      let message = `Ошибка ${response.status}`;
      try {
        const parsed: unknown = JSON.parse(text);
        if (typeof parsed === 'object' && parsed && 'error' in parsed) {
          message = String((parsed as { error: unknown }).error);
        }
      } catch {
        /* тело не JSON */
      }
      throw new ApiError(response.status, message);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `labels-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  provenance: (specimenId: string) =>
    request<{ entries: ProvenanceEntry[] }>(`/specimens/${specimenId}/provenance`),
  saveProvenance: (specimenId: string, entries: Record<string, unknown>[]) =>
    request<{ entries: ProvenanceEntry[] }>(`/specimens/${specimenId}/provenance`, {
      method: 'PUT',
      body: json({ entries }),
    }),

  countrySuggestions: (q: string) =>
    request<{ name: string; mine: boolean }[]>(
      `/countries/suggest?q=${encodeURIComponent(q)}`,
    ),
  /**
   * Оценка при покупке: что это и сколько стоит. Ничего не сохраняет —
   * ни снимков, ни образца, ни оценки.
   */
  appraise: async (photos: Blob[], fields: Record<string, string | null>): Promise<Appraisal> => {
    const form = new FormData();
    for (const photo of photos.slice(0, 3)) form.append('specimen', photo, 'photo.webp');
    for (const [key, value] of Object.entries(fields)) {
      if (value) form.append(key, value);
    }
    const response = await fetch('/api/appraise', {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
    });
    const text = await response.text();
    const body: unknown = text ? JSON.parse(text) : null;
    if (!response.ok) {
      throw new ApiError(
        response.status,
        typeof body === 'object' && body && 'error' in body
          ? String((body as { error: unknown }).error)
          : `Ошибка ${response.status}`,
      );
    }
    return body as Appraisal;
  },
  aiStatus: () => request<AiStatus>('/ai/status'),
  /**
   * Вопрос по снимкам, которых ещё нет на сервере: образец не сохранён,
   * поэтому файлы уходят прямо из формы и нигде не оседают.
   */
  identifyPhotos: async (photos: Blob[], localityName: string | null, country: string | null) => {
    const form = new FormData();
    for (const photo of photos.slice(0, 3)) form.append('specimen', photo, 'photo.webp');
    if (localityName) form.append('locality_name', localityName);
    if (country) form.append('locality_country', country);
    const response = await fetch('/api/identify/photo', {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
    });
    const text = await response.text();
    const body: unknown = text ? JSON.parse(text) : null;
    if (!response.ok) {
      throw new ApiError(
        response.status,
        typeof body === 'object' && body && 'error' in body
          ? String((body as { error: unknown }).error)
          : `Ошибка ${response.status}`,
      );
    }
    return body as {
      candidates: IdCandidate[];
      others: IdCandidate[];
      missing: string;
      model: string;
    };
  },
  identifications: (specimenId: string) =>
    request<Identification[]>(`/specimens/${specimenId}/identifications`),
  identify: (specimenId: string, hint: string | null) =>
    request<Identification>(`/specimens/${specimenId}/identify`, {
      method: 'POST',
      body: json({ hint }),
    }),
  acceptIdentification: (id: string, index: number, kind: 'candidate' | 'other' = 'candidate') =>
    request<{ ok: boolean; mineral_id: number }>(`/identifications/${id}/accept`, {
      method: 'POST',
      body: json({ index, kind }),
    }),
  deleteIdentification: (id: string) =>
    request<void>(`/identifications/${id}`, { method: 'DELETE' }),

  setCover: (photoId: string) => request<void>(`/photos/${photoId}/cover`, { method: 'POST' }),
  deletePhoto: (photoId: string) => request<void>(`/photos/${photoId}`, { method: 'DELETE' }),
};

/**
 * Адрес файла снимка. Прямая ссылка без сессии отдаёт 401 — снимки приватны
 * (ТЗ 13), поэтому каталог не раздаётся статикой.
 */
export function photoUrl(photoId: string, thumb = false): string {
  return `/api/photos/${photoId}${thumb ? '/thumb' : ''}`;
}

export const PHOTO_KIND_LABELS: Record<PhotoKind, string> = {
  specimen: 'образец',
  macro: 'макро',
  in_situ: 'в поле',
  label: 'этикетка',
  uv: 'люминесценция',
};

// ── Подписи для интерфейса ────────────────────────────────────────────

export const ACQUISITION_LABELS: Record<AcquisitionType, string> = {
  found: 'находка',
  purchased: 'покупка',
  traded: 'обмен',
  gift: 'подарок',
  inherited: 'унаследовано',
  unknown: 'неизвестно',
};

/** Формулировки по ТЗ 2.4, от самого надёжного к самому слабому. */
export const CONFIDENCE_LABELS: Record<Confidence, string> = {
  confirmed: 'лабораторно или экспертом',
  probable: 'определено визуально',
  uncertain: 'предположение',
  as_labelled: 'со слов продавца',
};

/**
 * Цвет чипа достоверности. Зелёным помечается только то, что реально
 * проверено: «со слов продавца» — самый слабый уровень, и красить его
 * как успех значит врать в единственном поле, ради которого каталог
 * отличается от списка красивых камней (ТЗ 2.4).
 */
export const CONFIDENCE_CHIP: Record<Confidence, string> = {
  confirmed: 'chip chip--ok',
  probable: 'chip chip--plain',
  uncertain: 'chip chip--warn',
  as_labelled: 'chip chip--warn',
};

/** Дата в виде, принятом на этикетке и в карточке: 06.2024 или 12.06.2024. */
export function formatDate(value: string | null, withDay = true): string {
  if (!value) return '';
  const [year, month, day] = value.slice(0, 10).split('-');
  return withDay && day ? `${day}.${month}.${year}` : `${month}.${year}`;
}

/**
 * Название минерала для показа: русское, если известно, с заглавной буквы.
 * Справочник Mindat весь на латинице, но каталог ведут по-русски.
 */
export function mineralTitle(
  mineral: { name: string; name_ru?: string | null } | null | undefined,
): string {
  if (!mineral) return '';
  const chosen = mineral.name_ru ?? mineral.name;
  return chosen ? chosen.charAt(0).toUpperCase() + chosen.slice(1) : '';
}

/**
 * Сумма с валютой: 12 500 ₽. Дробная часть показывается только когда она
 * есть — у камня цена круглая куда чаще, чем с копейками.
 */
export function formatMoney(amount: string | number | null, currency: string | null): string {
  if (amount === null || amount === undefined) return '';
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  const code = (currency ?? 'RUB').toUpperCase();
  try {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  } catch {
    // Неизвестный код валюты Intl не переварит, но показать сумму всё равно надо.
    return `${value.toLocaleString('ru-RU')} ${code}`;
  }
}

/**
 * Русское склонение после числа. Без него сводка выдаёт «1 стран»,
 * и приложение сразу выглядит недоделанным.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}
